import { supabase } from '../helper/supabaseClient';

/**
 * Normalized row for the UI + logging.
 * @typedef {Object} AvailableAction
 * @property {string} id
 * @property {string} actionKey
 * @property {string} title
 * @property {string|null} description
 * @property {string|null} category
 * @property {string|null} iconName
 * @property {boolean} requiresVerification
 * @property {boolean} requiresPhoto
 * @property {boolean} isHighImpact
 * @property {boolean} isGenericPrompt
 */

function cleanRow(row) {
  return {
    id: row.id,
    actionKey: row.action_key ?? '',
    title: row.title ?? '',
    description: row.description ?? null,
    category: row.category ?? null,
    iconName: row.icon_name ?? null,
    requiresVerification: Boolean(row.requires_verification),
    requiresPhoto: Boolean(row.requires_photo),
    isHighImpact: Boolean(row.is_high_impact),
    isGenericPrompt: Boolean(row.is_generic_prompt),
    sortOrder: row.sort_order ?? 0,
    createdAt: row.created_at ?? null,
    updatedAt: row.updated_at ?? null,
    points: 0,
  };
}

/**
 * Active climate actions from Supabase, ordered for display:
 * generic prompt first, then by sort_order, then newest first.
 */
export async function getAvailableActions() {
  const { data, error } = await supabase
    .from('available_actions')
    .select(
      [
        'id',
        'action_key',
        'title',
        'description',
        'category',
        'icon_name',
        'requires_verification',
        'requires_photo',
        'is_high_impact',
        'is_active',
        'is_generic_prompt',
        'sort_order',
        'created_at',
        'updated_at',
      ].join(',')
    )
    .eq('is_active', true)
    .order('is_generic_prompt', { ascending: false })
    .order('sort_order', { ascending: true })
    .order('created_at', { ascending: false });

  if (error) {
    throw new Error(error.message || 'Failed to load climate actions');
  }

  return (data ?? []).map(cleanRow);
}
